import { Avatar, Button, CircularProgress, Divider } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useOrder } from 'data/orders'
import { usePharmacy, usePharmacyOrderSettings } from 'data/pharmacy'
import ErrorFallback from 'components/ErrorFallback'
import { formatCurrency, formatDate, isItemOtc } from 'utils'
import { add, intervalToDuration, isBefore } from 'date-fns'
import { OrderDetail } from 'types/orders'
import { Fragment, useEffect, useState } from 'react'
import { useCancelOrder, useRepeatOrder } from 'mutations/orders'
import { useSnackbar } from 'notistack'
import { setLocalData } from 'hooks/useLocalData'
import { useDosageForms } from 'data/product'
import TopNav from 'components/TopNav'
import { Refresh } from 'components/PullToRefresh'

const completedStates = ['delivered', 'released', 'picked-up', 'completed', 'cancelled', 'failed']

export default function OrderDetails() {
  const { id } = useParams()
  const orderId = id as string
  const { data: order, isLoading, refetch, isError } = useOrder(orderId)
  const pharmacyId = order?.order.pharmacy_id as string
  const { data: pharmacy } = usePharmacy(pharmacyId)
  const [orderCountdown, setOrderCountdown] = useState<Duration>()
  const { data: fulfillmentSettings } = usePharmacyOrderSettings({
    pharmacy_id: pharmacy?.id as string,
    location_id: order?.order.location_id as string,
  })
  const cancelOrder = useCancelOrder()
  const repeatOrder = useRepeatOrder()
  const { enqueueSnackbar } = useSnackbar()
  const [hasPassed, setHasPassed] = useState(false)
  const navigate = useNavigate()
  const setData = setLocalData(['order_items'])
  const { data: dosageForms } = useDosageForms()

  function submitCancelOrder(id: string) {
    cancelOrder.mutate(id, {
      onSuccess: () => {
        enqueueSnackbar('Order cancelled successfully', { variant: 'success' })
      },
      onError: (e: any) => {
        enqueueSnackbar(e.message, { variant: 'error' })
      },
    })
  }

  function submitRepeatOrder(id: string) {
    repeatOrder.mutate(id, {
      onSuccess: (res) => {
        if (res.has_prescription_items) {
          setData(
            res.order_items
              .map((item: any) => {
                return (
                  item.requires_prescription && {
                    ...item,
                    requested_quantity: order?.items.find(
                      (i) => i.location_id === item.location_id && i.product_id === item.drug_id
                    )?.quantity,
                  }
                )
              })
              .filter(Boolean)
          )
          navigate('/easy-med?stage=other-doctor-prescription&from=reorder')
        } else {
          navigate(`/easy-med/cart`)
          enqueueSnackbar('Your order has been placed successfully', { variant: 'success' })
        }
      },
      onError: (e: any) => {
        if (e.message.toLowerCase() === 'Item not in stock.'.toLowerCase()) {
          setData(order?.items)
          navigate('/easy-med?stage=add-quantity')
          return
        }
        enqueueSnackbar(e.message, { variant: 'error' })
      },
    })
  }

  const range = fulfillmentSettings?.[0]?.config?.ranges?.find((setting) => {
    const itemCount = order?.items.reduce((acc, curr) => acc + curr.quantity, 0) || 0
    return itemCount >= setting.from && itemCount <= setting.to
  })

  useEffect(() => {
    if (!range || !order?.order.submitted_at) return

    const submittedAt = new Date(order.order.submitted_at)
    let readyAt: Date

    if (range.frequency === 'days') {
      readyAt = add(submittedAt, { days: range.duration })
    } else if (range.frequency === 'hours') {
      readyAt = add(submittedAt, { hours: range.duration })
    } else if (range.frequency === 'minutes') {
      readyAt = add(submittedAt, { minutes: range.duration })
    } else {
      readyAt = add(submittedAt, { days: range.duration })
    }

    const now = new Date()

    const countdown = isBefore(readyAt, now)
      ? { days: 0, hours: 0, minutes: 0, seconds: 0 }
      : intervalToDuration({ start: now, end: readyAt })

    setOrderCountdown(countdown)
  }, [order?.order.submitted_at, range])

  return (
    <>
      <TopNav title="Order" />
      <Refresh refetch={refetch}>
        <div className="rounded-xl md:p-5 p-3 border border-default">
          {isLoading ? (
            <div className="grid place-items-center mt-12 mb-10">
              <CircularProgress />
            </div>
          ) : isError ? (
            <ErrorFallback
              message="Something went wrong"
              description="We could not fetch orders"
              reset={refetch}
              buttonText="Try again"
            />
          ) : (
            <div>
              <div className="flex flex-col">
                <div className="">
                  <div className="flex justify-between pb-4">
                    <div className="space-y-3">
                      <p className=" font-semibold text-neutral-900 lg:block hidden">
                        Order code - {order.order.order_code}
                      </p>
                      <p className="lg:block hidden">Date - {formatDate(order.order.created_at)}</p>
                      {!completedStates.includes(order.order.order_status) &&
                        order.order.delivery_method === 'pickup' && (
                          <p className="">
                            Delivery code -{' '}
                            <span className="text-brand-600 font-bold">
                              {' '}
                              {order.order.delivery_method === 'pickup'
                                ? order.order.codes.pickup
                                : order.order.codes.delivery}
                            </span>
                          </p>
                        )}
                    </div>
                    <div className="space-y-3 flex flex-col justify-end ">
                      <div className="flex justify-center">
                        <Avatar
                          className="w-10 h-10 bg-brand-700"
                          alt={pharmacy?.pharmacy_name}
                          src={pharmacy?.logo_url}
                        />
                      </div>
                      <p className="text-center lg:text-lg text-xs">{pharmacy?.pharmacy_name}</p>
                      <p className="text-center lg:text-lg text-xs">{pharmacy?.pharmacy_address}</p>
                    </div>
                  </div>
                  <div className="lg:hidden border-t pt-3 pb-4 ">
                    <p className=" font-semibold text-neutral-900 my-3">
                      Order code - {order.order.order_code}
                    </p>
                    <p>Date - {formatDate(order.order.created_at)}</p>
                  </div>

                  {order.items?.map((item, index) => {
                    const measurement = dosageForms?.find(
                      (d) =>
                        d.measurement_unit === item?.dosage?.measurement ||
                        d.possible_dosage_units.includes(item.dosage?.measurement || '')
                    )

                    return (
                      <Fragment key={index}>
                        <div className=" flex justify-between border-t pt-5 pb-4">
                          <div className="space-y-3">
                            <p>Product - {item.product.product_name}</p>
                            <p>Dose - {item.product.metadata.dose}</p>
                            <p>Dosage form - {item.product.metadata.dosage_form}</p>
                            <p>
                              Unit price -{' '}
                              {formatCurrency(item.product_price - item.discount / item.quantity)}
                            </p>
                          </div>
                          <div className="space-y-3">
                            <p>Quantity - {item.quantity}</p>
                            <p>Brand - {item.product.brand}</p>
                            <p>Total Price - {formatCurrency(Number(item.cost - item.discount))}</p>
                          </div>
                        </div>
                        <div>
                          {item.dosage && (
                            <>
                              <div className="flex justify-between pb-4">
                                <p>
                                  Approved Qty -{' '}
                                  {item.dosage?.dispensed_quantity || item.dosage?.quantity}
                                </p>
                              </div>
                              {item.prescription_details?.doctor_details?.name && (
                                <div className="flex justify-between pb-4">
                                  <p>
                                    Prescribed By -{' '}
                                    {item.prescription_details?.doctor_details?.name}
                                  </p>
                                </div>
                              )}
                              <div className="flex justify-between pb-4">
                                <p>
                                  Medication Type -{' '}
                                  {!isItemOtc(item.product?.metadata?.is_otc) ? 'Prescription'
                                    : 'Over The Counter (OTC)'}
                                </p>
                              </div>

                              <div className="border rounded-lg ">
                                <div className="p-4 flex items-center ">
                                  <p className="text-neutral-900 font-semibold">
                                    Dosage Instruction
                                  </p>
                                </div>

                                <Divider />

                                <div className="p-4 space-y-4">
                                  <p className="text-lg text-brand-700">
                                    <span className="font-semibold text-neutral-500">
                                      {measurement?.command}
                                    </span>{' '}
                                    {item.dosage?.quantity}{' '}
                                    {measurement?.measurement_unit || 'Take'}
                                    <span className="font-semibold text-neutral-500">
                                      {' '}
                                      Every
                                    </span>{' '}
                                    {item.dosage?.interval} {item.dosage?.frequency}
                                    <span className="font-semibold text-neutral-500">
                                      {' '}
                                      For
                                    </span>{' '}
                                    {item.dosage?.duration} {item.dosage?.duration_frequency}
                                  </p>
                                  <p className="text-lg text-brand-700">
                                    <span className="font-semibold text-neutral-500">Route</span>{' '}
                                    {item.dosage?.route}
                                  </p>
                                </div>

                                {item.dosage?.notes && (
                                  <>
                                    <div className="px-4 py-6">
                                      <p className="font-semibold text-neutral-500 mb-2">Note</p>
                                      <div className="border bg-[#F6FFFF] rounded-xl p-4">
                                        <p>{item.dosage.notes}</p>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </Fragment>
                    )
                  })}
                </div>
                <div className="flex lg:flex-row flex-col justify-between pt-5">
                  <div className="mt-3">
                    {order?.order?.discount > 0 && (
                      <p className="mb-4">
                        Total Discount - {formatCurrency(Number(order?.order?.discount || 0))}
                      </p>
                    )}
                    <p className="mb-4">
                      Subtotal -{' '}
                      {order?.order?.discount > 0
                        ? formatCurrency(Number(order.order?.cost - order?.order?.discount))
                        : formatCurrency(Number(order.order?.cost))}
                    </p>
                    <p className="mb-4">
                      Processing Fee - {formatCurrency(order?.order?.processing_fee || 0)}
                    </p>
                    {order?.order.delivery_method === 'door' && (
                      <p className="mb-4">
                        Delivery Fee -{' '}
                        {formatCurrency(
                          order.order?.delivery_fee + (order.order?.delivery_markup || 0)
                        )}
                      </p>
                    )}

                    <p>
                      Total -{' '}
                      {order?.order?.discount > 0
                        ? formatCurrency(
                            Number(
                              order.order.total_cost -
                                order?.order?.discount +
                                (order.order.delivery_markup || 0)
                            )
                          )
                        : formatCurrency(
                            Number(order.order.total_cost + (order.order.delivery_markup || 0))
                          )}
                    </p>
                  </div>

                  {['submitted'].includes(order?.order?.order_status) && (
                    <div className="flex flex-col items-start lg:items-end space-y-2 lg:mt-3 mt-16">
                      {range ? (
                        <p className="lg:font-medium font-semibold">
                          Order should be ready in {range?.duration} {range?.frequency}
                        </p>
                      ) : (
                        <p className="lg:font-medium font-semibold">
                          Time to make order ready not set
                        </p>
                      )}
                      {orderCountdown && range && (
                        <Timer
                          countDown={orderCountdown}
                          order={order}
                          hasPassed={hasPassed}
                          alertTimerDone={(data) => {
                            setHasPassed(data)
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 my-4">
                  <Button
                    className="w-fit "
                    onClick={() => navigate(`/track-order/${order.order.id}`)}
                  >
                    Order History
                  </Button>

                  {((hasPassed && !['cancelled'].includes(order?.order.order_status)) ||
                    ['ready'].includes(order?.order.order_status as string)) && (
                    <Button
                      variant="contained"
                      className="w-fit  bg-[#EF4444]"
                      onClick={() => submitCancelOrder(order.order.id)}
                      disabled={cancelOrder.isLoading}
                    >
                      {cancelOrder.isLoading ? (
                        <CircularProgress className="text-white" />
                      ) : (
                        'Cancel Order'
                      )}
                    </Button>
                  )}
                  {completedStates.includes(order.order.order_status) && (
                    <div className="flex justify-end">
                      <Button
                        variant="outlined"
                        disabled={repeatOrder.isLoading}
                        onClick={() => submitRepeatOrder(orderId)}
                      >
                        {repeatOrder.isLoading ? <CircularProgress /> : ' Repeat Order '}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Refresh>
    </>
  )
}

function Timer(props: {
  countDown: Duration
  order: OrderDetail
  hasPassed: boolean
  alertTimerDone: (data: boolean) => void
}) {
  const { countDown, order, hasPassed, alertTimerDone } = props

  // Calculate total seconds from countDown
  const totalSeconds =
    (countDown.days || 0) * 86400 +
    (countDown.hours || 0) * 3600 +
    (countDown.minutes || 0) * 60 +
    (countDown.seconds || 0)

  const [timer, setTimer] = useState(totalSeconds)

  useEffect(() => {
    if (timer <= 0) {
      alertTimerDone(true)
      return
    }
    const interval = setInterval(() => {
      setTimer((prev) => Math.max(prev - 1, 0))
    }, 1000)
    return () => clearInterval(interval)
  }, [timer, alertTimerDone])

  // Format time units
  const days = Math.floor(timer / 86400)
  const hours = Math.floor((timer % 86400) / 3600)
  const minutes = Math.floor((timer % 3600) / 60)
  const seconds = timer % 60

  function renderTime(time: number) {
    if (
      hasPassed ||
      ['picked-up', 'ready', 'cancelled'].includes(order?.order.order_status as string)
    ) {
      return '00'
    }
    return time.toString().padStart(2, '0')
  }

  return (
    <div className={`flex space-x-4 h-[100px] ${timer <= 0 ? 'opacity-0' : ''}`}>
      {[
        { label: 'Days', value: days },
        { label: 'Hours', value: hours },
        { label: 'Minute', value: minutes },
        { label: 'Seconds', value: seconds },
      ].map((item) => (
        <div key={item.label} className="grid place-items-center space-y-4 text-sm font-medium">
          <div className="rounded-full w-11 h-11 grid text-xs place-items-center border-brand-700 border-4">
            {renderTime(item.value)}
          </div>
          <p>{item.label}</p>
        </div>
      ))}
    </div>
  )
}
